import React, { useEffect, useState } from "react";
import { ContactAdminCard, MethodToggles } from "@/components/ContactAdmin";
import axios from "axios";
import { API, useAuth, formatApiError } from "@/auth";
import {
  Smartphone, Wallet, Coins, CreditCard, Loader2, CheckCircle2, XCircle,
  Copy, RefreshCw, Send, Settings, ShieldCheck, Clock, Upload, Download, Trash2, Package, Phone,
} from "lucide-react";

const METHODS = [
  { id: "airtel", label: "Airtel Money", icon: Smartphone, color: "#E60000" },
  { id: "orange", label: "Orange Money", icon: Wallet, color: "#FF7900" },
  { id: "binance", label: "Binance Pay", icon: Coins, color: "#F0B90B" },
  { id: "visa",   label: "Visa / Carte", icon: CreditCard, color: "#1A1F71" },
];

export default function PaiementsPage() {
  const { user, refreshMe } = useAuth();
  const isAdmin = user?.role === "admin";
  const [merchant, setMerchant] = useState(null);
  const [tab, setTab] = useState(isAdmin ? "admin" : "buy");
  const [plan, setPlan] = useState("1M");
  const [method, setMethod] = useState("airtel");
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponMsg, setCouponMsg] = useState(null);
  const [whatsapp, setWhatsapp] = useState("");
  const [current, setCurrent] = useState(null);
  const [txId, setTxId] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [mcfg, setMcfg] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", percent_off: 10, max_uses: 10 });

  const loadAll = async () => {
    try {
      const cfg = await axios.get(`${API}/merchant/config`);
      setMerchant(cfg.data);
      setMcfg(cfg.data);
      const my = await axios.get(`${API}/payments/mine`);
      setMine(my.data);
      if (isAdmin) {
        const all = await axios.get(`${API}/payments`);
        setPending(all.data);
        const cs = await axios.get(`${API}/coupons`);
        setCoupons(cs.data);
      }
    } catch (e) { console.warn("loadAll payments failed", e?.message); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const validateCoupon = async () => {
    setCouponMsg(null);
    if (!couponInput.trim()) return;
    try {
      const { data } = await axios.get(`${API}/coupons/validate/${couponInput.trim().toUpperCase()}`);
      setCoupon(data);
      setCouponMsg({ ok: true, text: `-${data.percent_off}% ${data.code}` });
    } catch (err) {
      setCoupon(null);
      setCouponMsg({ ok: false, text: formatApiError(err) });
    }
  };

  const initPayment = async () => {
    setBusy(true); setMsg(null); setCurrent(null);
    try {
      const { data } = await axios.post(`${API}/payments/init`, {
        plan, method,
        coupon: coupon?.code || null,
        whatsapp: whatsapp || null,
      });
      setCurrent(data);
    } catch (err) { setMsg({ ok: false, text: formatApiError(err) }); }
    finally { setBusy(false); }
  };

  const submitProof = async () => {
    if (!current || !txId.trim()) { setMsg({ ok: false, text: "Entrez le Transaction ID" }); return; }
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.post(`${API}/payments/${current.id}/submit`, {
        transaction_id: txId.trim(), payer_phone: phone || null, note: note || null,
      });
      setCurrent(data);
      setMsg({ ok: true, text: "Preuve envoyée. En attente de validation admin." });
      loadAll();
    } catch (err) { setMsg({ ok: false, text: formatApiError(err) }); }
    finally { setBusy(false); }
  };

  const review = async (pid, action, reason) => {
    await axios.post(`${API}/payments/${pid}/review`, { action, reason });
    await refreshMe();
    loadAll();
  };

  const saveMerchant = async () => {
    await axios.patch(`${API}/merchant/config`, mcfg);
    loadAll();
  };

  const copy = (t) => navigator.clipboard.writeText(t);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="payments-page">
      <div className="card-lhs p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
              <CreditCard className="w-6 h-6 text-orange-500" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-tech">Paiements</div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Achat & Renouvellement</h1>
              <p className="text-zinc-400 text-sm mt-1">Airtel Money · Orange Money · Binance Pay</p>
            </div>
          </div>
          <div className="flex rounded-lg overflow-x-auto scroll-thin border border-zinc-800 w-full lg:w-auto">
            <TabBtn active={tab === "buy"} onClick={() => setTab("buy")} testid="tab-buy">Acheter</TabBtn>
            <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} testid="tab-mine">Mes paiements</TabBtn>
            {isAdmin && <TabBtn active={tab === "admin"} onClick={() => setTab("admin")} testid="tab-admin">Admin</TabBtn>}
          </div>
        </div>
      </div>

      {tab === "buy" && (
        <div className="space-y-6" data-testid="buy-section">
          <div className="card-lhs p-5 sm:p-7">
            <h2 className="font-display font-bold uppercase tracking-wider text-sm mb-1">Moyens de paiement acceptés</h2>
            <p className="text-xs text-zinc-500 mb-5">
              Les tarifs et les instructions de paiement vous sont communiqués directement par l'administrateur.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="payment-methods-logos">
              {METHODS.filter(m => (merchant?.enabled_methods || []).includes(m.id)).map(m => (
                <div key={m.id} data-testid={`pay-method-${m.id}`} className="rounded-xl border border-zinc-800 bg-[#0C0C10] p-4 flex flex-col items-center gap-2 text-center">
                  <m.icon className="w-10 h-10" style={{ color: m.color }} />
                  <span className="text-xs font-semibold">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
          <ContactAdminCard merchant={merchant} onRequested={loadAll} />
        </div>
      )}

      {tab === "mine" && (
        <PaymentsTable rows={mine} testid="mine-list" />
      )}

      {tab === "admin" && isAdmin && (
        <div className="space-y-6">
          <div className="card-lhs p-5" data-testid="merchant-config">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-orange-500" />
              <h2 className="font-display font-bold uppercase tracking-wider text-sm">Config marchand</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Airtel Money N°" v={mcfg.airtel_number} k="airtel_number" onChange={setMcfg} state={mcfg} testid="cfg-airtel" />
              <Field label="Orange Money N°" v={mcfg.orange_number} k="orange_number" onChange={setMcfg} state={mcfg} testid="cfg-orange" />
              <Field label="Binance Pay ID" v={mcfg.binance_pay_id} k="binance_pay_id" onChange={setMcfg} state={mcfg} testid="cfg-binance-id" />
              <Field label="Binance Address" v={mcfg.binance_address} k="binance_address" onChange={setMcfg} state={mcfg} testid="cfg-binance-addr" />
              <Field label="Devise" v={mcfg.currency} k="currency" onChange={setMcfg} state={mcfg} testid="cfg-currency" />
              <div />
              <Field label="WhatsApp admin" v={mcfg.admin_whatsapp} k="admin_whatsapp" onChange={setMcfg} state={mcfg} testid="cfg-admin-wa" />
              <Field label="Telegram admin" v={mcfg.admin_telegram} k="admin_telegram" onChange={setMcfg} state={mcfg} testid="cfg-admin-tg" />
              <Field label="Email admin" v={mcfg.admin_email} k="admin_email" onChange={setMcfg} state={mcfg} testid="cfg-admin-email" />
              <Field label="Prix 1 Mois" v={mcfg.price_1m} k="price_1m" type="number" onChange={setMcfg} state={mcfg} testid="cfg-p1" />
              <Field label="Prix 3 Mois" v={mcfg.price_3m} k="price_3m" type="number" onChange={setMcfg} state={mcfg} testid="cfg-p3" />
              <Field label="Prix 6 Mois" v={mcfg.price_6m} k="price_6m" type="number" onChange={setMcfg} state={mcfg} testid="cfg-p6" />
              <Field label="Prix 12 Mois" v={mcfg.price_12m} k="price_12m" type="number" onChange={setMcfg} state={mcfg} testid="cfg-p12" />
              <Field label="URL téléchargement client Windows" v={mcfg.app_download_url} k="app_download_url" onChange={setMcfg} state={mcfg} testid="cfg-download" />
              <Field label="Version app" v={mcfg.app_version} k="app_version" onChange={setMcfg} state={mcfg} testid="cfg-version" />
              <Field label="Tolérance hors-ligne (heures)" v={mcfg.offline_tolerance_hours} k="offline_tolerance_hours" type="number" onChange={setMcfg} state={mcfg} testid="cfg-offline-tol" />
            </div>
            <MethodToggles state={mcfg} onChange={setMcfg} />
            <button data-testid="cfg-save" onClick={saveMerchant} className="btn-orange rounded-lg px-4 py-2 text-sm mt-4">Enregistrer</button>
          </div>

          <InstallerCard onChange={loadAll} />

          {/* Coupons management */}
          <div className="card-lhs p-5" data-testid="coupons-admin">
            <h2 className="font-display font-bold uppercase tracking-wider text-sm mb-4">Codes promo</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              <input data-testid="coupon-code" className="input-lhs w-52 uppercase" placeholder="Code (ex: LHSPROMO)"
                value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} />
              <input data-testid="coupon-percent" type="number" min={1} max={90} className="input-lhs w-24" placeholder="% off"
                value={newCoupon.percent_off} onChange={(e) => setNewCoupon({ ...newCoupon, percent_off: Number(e.target.value) })} />
              <input data-testid="coupon-max" type="number" min={1} className="input-lhs w-24" placeholder="Max"
                value={newCoupon.max_uses} onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: Number(e.target.value) })} />
              <button data-testid="coupon-create-btn" onClick={async () => {
                try {
                  await axios.post(`${API}/coupons`, newCoupon);
                  setNewCoupon({ code: "", percent_off: 10, max_uses: 10 });
                  loadAll();
                } catch (err) { alert(formatApiError(err)); }
              }} className="btn-orange px-4 py-2 rounded-lg text-sm">Créer</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto scroll-thin">
              {coupons.length === 0 && <div className="text-sm text-zinc-500 italic col-span-full">Aucun code promo.</div>}
              {coupons.map(c => (
                <div key={c.code} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-[#0C0C10] p-3 text-xs">
                  <div>
                    <div className="font-mono-tech text-orange-400 font-bold">{c.code}</div>
                    <div className="text-zinc-500 mt-0.5">-{c.percent_off}% · {c.used_count}/{c.max_uses} utilisés</div>
                  </div>
                  <button onClick={async () => {
                    if (!window.confirm("Supprimer ce code ?")) return;
                    await axios.delete(`${API}/coupons/${c.code}`); loadAll();
                  }} className="text-zinc-500 hover:text-red-400">
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <PaymentsAdminTable rows={pending} onReview={review} />
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, children, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2 text-xs uppercase tracking-wider font-mono-tech ${active ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-white"}`}>
      {children}
    </button>
  );
}

function Field({ label, v, k, type = "text", onChange, state, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase text-zinc-500 mb-1 font-mono-tech">{label}</label>
      <input data-testid={testid} type={type} className="input-lhs" value={v ?? ""}
        onChange={(e) => onChange({ ...state, [k]: type === "number" ? Number(e.target.value) : e.target.value })} />
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    contact_requested: ["text-sky-400 bg-sky-500/10 border-sky-500/40", "Demande envoyée"],
    pending_payment: ["text-zinc-400 bg-zinc-500/10 border-zinc-500/30", "En attente paiement"],
    pending_review: ["text-amber-400 bg-amber-500/10 border-amber-500/40", "À valider"],
    approved: ["text-emerald-400 bg-emerald-500/10 border-emerald-500/40", "Approuvé"],
    rejected: ["text-red-400 bg-red-500/10 border-red-500/40", "Rejeté"],
  };
  const [cls, lbl] = map[s] || ["text-zinc-400", s];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono-tech uppercase ${cls}`}>{lbl}</span>;
}

function PaymentsTable({ rows, testid }) {
  return (
    <div className="card-lhs overflow-hidden" data-testid={testid}>
      {/* Desktop table */}
      <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-mono-tech border-b border-zinc-800">
        <div className="col-span-2">Date</div>
        <div className="col-span-2">Référence</div>
        <div className="col-span-1">Formule</div>
        <div className="col-span-1">Moyen</div>
        <div className="col-span-2">Montant</div>
        <div className="col-span-2">Transaction ID</div>
        <div className="col-span-2 text-right">Statut</div>
      </div>
      <div className="divide-y divide-zinc-800/70 max-h-[60vh] overflow-y-auto scroll-thin">
        {rows.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">Aucun paiement.</div>}
        {rows.map(r => (
          <div key={r.id} className="hover:bg-zinc-900/50">
            {/* Desktop row */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-xs items-center">
              <div className="col-span-2 font-mono-tech text-zinc-400">{new Date(r.created_at).toLocaleString("fr-FR")}</div>
              <div className="col-span-2 font-mono-tech text-orange-400">{r.reference}</div>
              <div className="col-span-1">{r.plan || "—"}</div>
              <div className="col-span-1 uppercase text-zinc-400">{r.method || "—"}</div>
              <div className="col-span-2">{r.amount != null ? `${r.amount} ${r.currency}` : "—"}</div>
              <div className="col-span-2 font-mono-tech text-zinc-500 truncate">{r.transaction_id || "—"}</div>
              <div className="col-span-2 flex justify-end"><StatusPill s={r.status} /></div>
            </div>
            {/* Mobile card */}
            <div className="md:hidden p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono-tech text-orange-400 text-xs truncate">{r.reference}</span>
                <StatusPill s={r.status} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-zinc-500">Formule:</span> <span className="text-white font-mono-tech">{r.plan}</span></div>
                <div><span className="text-zinc-500">Moyen:</span> <span className="text-white uppercase">{r.method}</span></div>
                <div><span className="text-zinc-500">Montant:</span> <span className="text-white font-mono-tech">{r.amount} {r.currency}</span></div>
                <div><span className="text-zinc-500">Date:</span> <span className="text-white font-mono-tech">{new Date(r.created_at).toLocaleDateString("fr-FR")}</span></div>
              </div>
              {r.transaction_id && (
                <div className="text-xs"><span className="text-zinc-500">TX:</span> <span className="font-mono-tech text-zinc-400 break-all">{r.transaction_id}</span></div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentsAdminTable({ rows, onReview }) {
  const [filter, setFilter] = useState("pending_review");
  const filtered = filter === "all" ? rows : rows.filter(r => r.status === filter);
  return (
    <div className="card-lhs p-5" data-testid="admin-payments-table">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold uppercase tracking-wider text-sm">Demandes de paiement</h2>
        <div className="flex rounded-lg overflow-hidden border border-zinc-800">
          {["contact_requested","pending_review","pending_payment","approved","rejected","all"].map(t => (
            <button key={t} onClick={() => setFilter(t)}
              className={`px-3 py-1.5 text-[10px] uppercase font-mono-tech ${filter===t?"bg-orange-500 text-black":"text-zinc-400 hover:text-white"}`}>
              {t.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2 max-h-[60vh] overflow-y-auto scroll-thin">
        {filtered.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">Aucune demande.</div>}
        {filtered.map(r => (
          <div key={r.id} className="rounded-lg border border-zinc-800 bg-[#0C0C10] p-3" data-testid={`pay-row-${r.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono-tech text-orange-400">{r.reference}</span>
                  <StatusPill s={r.status} />
                  <span className="text-zinc-500 text-xs">{r.plan} · {r.method}</span>
                </div>
                <div className="text-xs text-zinc-400 font-mono-tech mt-1">
                  {r.user_name} · {r.user_email} · <span className="text-white">{r.amount} {r.currency}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1 font-mono-tech">
                  TxID: <span className="text-zinc-300">{r.transaction_id || "—"}</span>
                  {r.payer_phone && <> · Tel: {r.payer_phone}</>}
                  {r.note && <> · {r.note}</>}
                </div>
                {r.license_key && <div className="text-[11px] text-emerald-400 mt-1 font-mono-tech">Licence: {r.license_key}</div>}
              </div>
              {(r.status === "pending_review" || r.status === "pending_payment") && (
                <div className="flex gap-2">
                  <button data-testid={`approve-${r.id}`} onClick={() => onReview(r.id, "approve")}
                    className="btn-orange px-3 py-1.5 rounded-lg text-xs flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approuver
                  </button>
                  <button data-testid={`reject-${r.id}`} onClick={() => {
                    const reason = window.prompt("Raison du rejet ?", "");
                    if (reason !== null) onReview(r.id, "reject", reason);
                  }}
                    className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 border border-red-500/40 text-red-400 hover:bg-red-500/10">
                    <XCircle className="w-3.5 h-3.5" /> Rejeter
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InstallerCard({ onChange }) {
  const [info, setInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/app/config`);
      setInfo(data);
      if (data.app_version) setVersion(data.app_version);
    } catch (e) { console.warn("app config load failed", e?.message); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) { setMsg({ ok: false, text: "Sélectionnez un fichier .exe" }); return; }
    setBusy(true); setMsg(null);
    const fd = new FormData();
    fd.append("file", file);
    if (version) fd.append("version", version);
    try {
      await axios.post(`${API}/app/installer/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ ok: true, text: "Installateur mis en ligne avec succès." });
      setFile(null);
      await load();
      onChange && onChange();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Échec du téléversement" });
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer l'installateur actuel ?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/app/installer`);
      await load();
      onChange && onChange();
    } finally { setBusy(false); }
  };

  const download = () => {
    window.open(`${API}/app/installer/download`, "_blank");
  };

  return (
    <div className="card-lhs p-5" data-testid="installer-card">
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-4 h-4 text-orange-500" />
        <h2 className="font-display font-bold uppercase tracking-wider text-sm">Setup du logiciel LHSunlocker</h2>
      </div>

      {info?.installer_uploaded ? (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 mb-4" data-testid="installer-current">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-mono-tech mb-1">Actuel · v{info.app_version}</div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <Package className="w-4 h-4 text-emerald-400" />
                <span className="truncate">{info.installer_filename}</span>
              </div>
              <div className="text-[11px] text-zinc-400 font-mono-tech mt-1">
                {(info.installer_size_bytes / (1024 * 1024)).toFixed(1)} MB · uploadé {new Date(info.installer_uploaded_at).toLocaleString("fr-FR")}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                URL publique : <code className="text-orange-400">/api/app/installer/download</code>
              </div>
              {info.installer_sha256 && (
                <div className="text-[10px] text-zinc-500 mt-2 font-mono-tech flex items-start gap-2">
                  <span className="text-emerald-400 shrink-0">SHA-256 :</span>
                  <code className="text-zinc-400 break-all">{info.installer_sha256}</code>
                  <button onClick={() => navigator.clipboard.writeText(info.installer_sha256)} className="text-zinc-500 hover:text-orange-400 shrink-0" title="Copier">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button data-testid="installer-download-btn" onClick={download} className="btn-orange px-3 py-2 rounded-lg text-xs flex items-center gap-2">
                <Download className="w-3.5 h-3.5" /> Télécharger
              </button>
              <button data-testid="installer-delete-btn" onClick={del} disabled={busy}
                className="px-3 py-2 rounded-lg text-xs border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-800 bg-[#0C0C10] p-4 mb-4 text-sm text-zinc-400" data-testid="installer-none">
          Aucun installateur en ligne. Uploadez le fichier <code className="text-orange-400">LHSunlocker Setup.exe</code> ci-dessous.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          data-testid="installer-file"
          type="file"
          accept=".exe,.msi,.zip,.7z"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="input-lhs text-xs md:col-span-2"
        />
        <input
          data-testid="installer-version"
          placeholder="Version (ex: 1.2.0)"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          className="input-lhs md:col-span-1"
        />
        <button data-testid="installer-upload-btn" onClick={upload} disabled={busy || !file}
          className="btn-orange rounded-lg text-sm flex items-center justify-center gap-2 md:col-span-1">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Envoyer
        </button>
      </div>
      {msg && (
        <div className={`mt-3 text-sm px-3 py-2 rounded-lg border ${msg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-red-400 border-red-500/40 bg-red-500/10"}`}>
          {msg.text}
        </div>
      )}
      <p className="text-[11px] text-zinc-500 mt-2">
        Formats acceptés : .exe / .msi / .zip / .7z · Taille max 500 MB · Une fois uploadé, le bouton <strong>Télécharger le client Windows</strong> apparaît automatiquement sur la page de connexion pour tous les visiteurs.
      </p>
    </div>
  );
}

